import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  getReimbursements, createReimbursement,
  approveReimbursement, rejectReimbursement, deleteReimbursement,
  reviewReimbursement, returnReimbursement, resubmitReimbursement, reopenReimbursement, updateReimbursementItem,
  addReimbursementItem, deleteReimbursementItem,
  uploadReimbursementAttachment, deleteReimbursementAttachment,
  exportReimbursements,
  markReimbursementPaid, bulkUpdateReimbursements,
  getExpenseCategories, getTasks, getStaff, getClients, getReimbursement,
  extractReceiptOCR, uploadsUrl,
  // getClients used only inside ClientSearch component via live search
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import DateRangeFilter, { inDateRange } from '../components/DateRangeFilter';
import { extractErrorMessage } from '../utils/errorMessage';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileReimbursements from './mobile/MobileReimbursements';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

// ── Types ────────────────────────────────────────────────────────────────────

interface ExpenseCategory { id: number; name: string; }
interface ClientOption { id: number; clientCode: string; clientName: string; }
// categoryName rides along so the task picker can disambiguate two open tasks
// that share a name — a client with both "FY27" retainer and "FY27" advisory
// is common, and picking the wrong one files the claim's OPE against a task
// that will never be invoiced.
interface TaskOption { id: number; taskId: string; taskName: string; status: string; clientId?: number; categoryName?: string; }
interface StaffInfo { id: number; staffName: string; isPartner?: boolean; }

interface RAttachment { id: number; fileName: string; originalName: string; fileSize: number; mimeType: string; }
// Whether this exact expense has been recovered from the client, resolved
// server-side through Expense → InvoiceLineItem → Invoice. `billable` is false
// until the claim is approved, because that is when the Expense is minted.
interface RItemBilling {
  billable: boolean; billed: boolean;
  invoiceId: number | null; invoiceNumber: string | null;
  invoiceDate: string | null; invoiceStatus: string | null;
  billedAmount: number | null;
}
interface RItem {
  id: number; description: string; amount: number; date: string;
  category: ExpenseCategory; attachments: RAttachment[];
  billing?: RItemBilling;
}
type BillingStatus = 'BILLED' | 'PARTIAL' | 'UNBILLED' | 'NA';
interface Reimbursement {
  id: number; claimNumber: string;
  staff: { id: number; staffName: string };
  task: {
    id: number; taskId: string; taskName: string; status: string;
    client?: { id: number; clientName: string } | null;
    category?: { id: number; name: string } | null;
  };
  notes?: string;
  status: 'PENDING' | 'REVIEWED' | 'RETURNED' | 'APPROVED' | 'REJECTED' | 'PAID';
  reviewedBy?: { id: number; staffName: string };
  reviewedAt?: string;
  returnReason?: string;
  approvedBy?: { id: number; staffName: string };
  approvedAt?: string;
  rejectionReason?: string;
  paidOn?: string;
  paymentMode?: string;
  paymentReference?: string;
  items: RItem[];
  billingStatus?: BillingStatus;
  billedAmount?: number;
  billedInvoiceNumbers?: string[];
  createdAt: string;
  updatedAt: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending Review', REVIEWED: 'Reviewed', RETURNED: 'Returned',
  APPROVED: 'Approved', REJECTED: 'Rejected', PAID: 'Paid',
};
const STATUS_COLOR: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  REVIEWED: 'bg-blue-100 text-blue-700',
  RETURNED: 'bg-orange-100 text-orange-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  PAID: 'bg-emerald-100 text-emerald-800',
};
// Has the firm recovered this claim from the client yet? 'NA' is a claim that
// has not been approved, so there is no expense to bill in the first place.
const BILLING_LABEL: Record<BillingStatus, string> = {
  BILLED: 'Billed', PARTIAL: 'Partial', UNBILLED: 'Not billed', NA: '—',
};
const BILLING_COLOR: Record<BillingStatus, string> = {
  BILLED: 'bg-emerald-100 text-emerald-800',
  PARTIAL: 'bg-amber-100 text-amber-800',
  UNBILLED: 'bg-gray-100 text-gray-600',
  NA: 'bg-transparent text-gray-400',
};
// Sort order runs "most needs attention" first: an unbilled claim is money the
// firm has paid out and not yet recovered.
const BILLING_RANK: Record<BillingStatus, number> = { UNBILLED: 0, PARTIAL: 1, BILLED: 2, NA: 3 };

const fmt = (n: number) => '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDT = (d?: string) => d ? format(new Date(d), 'dd-MMM-yy HH:mm') : '—';
const fmtD = (d?: string) => d ? format(new Date(d), 'dd-MMM-yy') : '';
const claimTotal = (r: Reimbursement) => r.items.reduce((s, i) => s + Number(i.amount), 0);
const emptyItem = (date: string) => ({ description: '', amount: '', date, categoryId: '' });

// ── Client Search Input ────────────────────────────────────────────────────────

// ClientSearch — does live API search so staff don't need full client list access
const ClientSearch: React.FC<{
  value: number | '';
  initialLabel?: string;
  onChange: (id: number | '', client?: ClientOption) => void;
}> = ({ value, initialLabel, onChange }) => {
  const [query, setQuery] = useState(initialLabel || '');
  const [results, setResults] = useState<ClientOption[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Sync label when external value cleared
  useEffect(() => {
    if (value === '') setQuery('');
  }, [value]);

  useEffect(() => {
    if (initialLabel) setQuery(initialLabel);
  }, [initialLabel]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = async (q: string) => {
    setQuery(q);
    onChange('');
    if (q.trim().length >= 2) {
      try {
        const res = await getClients(q.trim());
        setResults(res.data);
        setOpen(true);
      } catch {
        setResults([]);
      }
    } else {
      setResults([]);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <input
        className="input-field"
        placeholder="Type client name or code (min 2 chars)..."
        value={query}
        onChange={e => handleChange(e.target.value)}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {results.map(c => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex gap-2"
              onMouseDown={() => {
                onChange(c.id, c);
                setQuery(`${c.clientCode} — ${c.clientName}`);
                setOpen(false);
              }}
            >
              <span className="font-mono text-xs text-blue-600 shrink-0">{c.clientCode}</span>
              <span className="text-gray-800 truncate">{c.clientName}</span>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && query.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow p-3 text-sm text-gray-400">
          No clients found for "{query}"
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const DesktopReimbursements: React.FC = () => {
  const { user, isAdmin, isHR } = useAuth();
  const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [allTasks, setAllTasks] = useState<TaskOption[]>([]);
  const [staff, setStaff] = useState<StaffInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [rSearch, setRSearch] = useState('');
  const [submittedFrom, setSubmittedFrom] = useState('');
  const [submittedTo, setSubmittedTo] = useState('');
  const [reimbSearchParams] = useSearchParams();

  // Apply filter from URL query param on mount (e.g. /reimbursements?status=PENDING)
  useEffect(() => {
    const status = reimbSearchParams.get('status');
    if (status) setFilterStatus(status);
    // eslint-disable-next-line
  }, []);
  const [sortField, setSortField] = useState<'claimNumber' | 'staff' | 'total' | 'billing' | 'status' | 'createdAt'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Create claim modal
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const [claimForm, setClaimForm] = useState({
    taskId: '', notes: '',
    items: [emptyItem(format(new Date(), 'yyyy-MM-dd'))],
  });
  const [itemFiles, setItemFiles] = useState<Record<number, File[]>>({});

  // Detail modal
  const [detail, setDetail] = useState<Reimbursement | null>(null);

  // Add item in detail modal
  const [addItemForm, setAddItemForm] = useState(emptyItem(format(new Date(), 'yyyy-MM-dd')));
  const [addItemFiles, setAddItemFiles] = useState<File[]>([]);
  const [addItemError, setAddItemError] = useState('');
  // When set, the line-item form below edits this existing item instead of adding a new one.
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const addFileRef = useRef<HTMLInputElement>(null);
  const ocrFileRef = useRef<HTMLInputElement>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState<string>('');

  // Modals
  const [returnModal, setReturnModal] = useState<number | null>(null);
  const [returnReason, setReturnReason] = useState('');
  const [rejectModal, setRejectModal] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Mark paid modal (single or bulk via selectedIds)
  const [paidModal, setPaidModal] = useState<{ ids: number[]; isBulk: boolean } | null>(null);
  const [paidOn, setPaidOn] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentMode, setPaymentMode] = useState('BANK');
  const [paymentReference, setPaymentReference] = useState('');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  const isPartnerOrAdmin = isAdmin || !!staff.find(s => s.id === user?.staffId)?.isPartner;

  // Who filed the claim. Shown whenever the list can hold someone else's: a
  // task manager sees every claim filed against a task they manage (see
  // buildReimbursementScope), and without this column they could not tell
  // whose it was. Hidden for someone looking only at their own claims, where
  // it would repeat their name on every row.
  const showStaffColumn =
    isPartnerOrAdmin || isHR || reimbursements.some(r => r.staff.id !== user?.staffId);

  // Tasks filtered to selected client (open only)
  const filteredTasks = selectedClientId
    ? allTasks.filter(t => t.clientId != null && t.clientId === selectedClientId)
    : [];

  const fetchAll = useCallback(async () => {
    const [rRes, tRes, sRes, cRes] = await Promise.all([
      getReimbursements(), getTasks(), getStaff(), getExpenseCategories(),
    ]);
    setReimbursements(rRes.data);
    setAllTasks(tRes.data
      .filter((t: any) => t.status === 'OPEN')
      .map((t: any) => ({ id: t.id, taskId: t.taskId, taskName: t.taskName, status: t.status, clientId: t.clientId, categoryName: t.category?.name }))
    );
    setStaff(sRes.data);
    setCategories(cRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshDetail = async (id: number) => {
    await fetchAll();
    const res = await getReimbursement(id);
    setDetail(res.data);
  };

  // Open the detail modal from a (possibly stale) list row, then immediately
  // re-fetch the claim so the action buttons reflect its CURRENT status. Without
  // this, a row cached as PENDING could still offer "Mark Reviewed" after another
  // reviewer (or an earlier action) already moved it to REVIEWED — the server
  // then rejects the action with a confusing "Claim is REVIEWED" error.
  const openDetail = async (r: Reimbursement) => {
    setDetail(r); // show instantly
    try {
      const res = await getReimbursement(r.id);
      setDetail(res.data);
    } catch {
      // Claim may have been deleted; keep the optimistic copy.
    }
  };

  // ── Reset create form ───────────────────────────────────────────────────────

  const openCreate = () => {
    setSelectedClientId('');
    setClaimForm({ taskId: '', notes: '', items: [emptyItem(format(new Date(), 'yyyy-MM-dd'))] });
    setItemFiles({});
    setCreateError('');
    setShowCreate(true);
  };

  // When client changes, clear task selection
  const handleClientChange = (id: number | '') => {
    setSelectedClientId(id);
    setClaimForm(f => ({ ...f, taskId: '' }));
  };

  // ── Create claim ────────────────────────────────────────────────────────────

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    if (!selectedClientId) { setCreateError('Please select a client'); return; }
    if (!claimForm.taskId) { setCreateError('Please select a task'); return; }
    const validItems = claimForm.items.filter(i => i.description && i.amount && i.date && i.categoryId);
    if (validItems.length === 0) { setCreateError('Add at least one complete line item'); return; }
    setCreating(true);
    try {
      const payload = {
        taskId: claimForm.taskId,
        notes: claimForm.notes,
        items: validItems.map(i => ({
          description: i.description,
          amount: Number(i.amount),
          date: i.date,
          categoryId: Number(i.categoryId),
        })),
      };
      const res = await createReimbursement(payload);
      const created: Reimbursement = res.data;
      // Upload attachments
      for (let idx = 0; idx < claimForm.items.length; idx++) {
        const files = itemFiles[idx] || [];
        const serverItem = created.items[idx];
        if (!serverItem) continue;
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          await uploadReimbursementAttachment(serverItem.id, fd);
        }
      }
      setShowCreate(false);
      fetchAll();
    } catch (err: any) {
      setCreateError(err.response?.data?.message || 'Error submitting claim');
    } finally { setCreating(false); }
  };

  // ── Add item to existing claim ──────────────────────────────────────────────

  const handleAddItem = async () => {
    if (!detail) return;
    if (!addItemForm.description || !addItemForm.amount || !addItemForm.date || !addItemForm.categoryId) {
      setAddItemError('Fill in all fields'); return;
    }
    setAddItemError('');
    try {
      const payload = {
        description: addItemForm.description,
        amount: Number(addItemForm.amount),
        date: addItemForm.date,
        categoryId: Number(addItemForm.categoryId),
      };
      const res = editingItemId
        ? await updateReimbursementItem(editingItemId, payload)
        : await addReimbursementItem(detail.id, payload);
      for (const file of addItemFiles) {
        const fd = new FormData();
        fd.append('file', file);
        await uploadReimbursementAttachment(res.data.id, fd);
      }
      setAddItemForm(emptyItem(format(new Date(), 'yyyy-MM-dd')));
      setAddItemFiles([]);
      setEditingItemId(null);
      if (addFileRef.current) addFileRef.current.value = '';
      await refreshDetail(detail.id);
    } catch (err: any) { setAddItemError(err.response?.data?.message || 'Error saving item'); }
  };

  const startEditItem = (item: any) => {
    setEditingItemId(item.id);
    setAddItemError('');
    setAddItemForm({
      description: item.description,
      amount: String(item.amount),
      date: String(item.date).slice(0, 10),
      categoryId: String(item.category?.id ?? item.categoryId ?? ''),
    });
  };
  const cancelEditItem = () => {
    setEditingItemId(null);
    setAddItemForm(emptyItem(format(new Date(), 'yyyy-MM-dd')));
    setAddItemFiles([]);
    setAddItemError('');
  };

  const handleOcrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    setOcrResult('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await extractReceiptOCR(fd);
      const d = res.data;
      setAddItemForm((prev) => ({
        ...prev,
        description: d.merchant || prev.description,
        amount: d.amount ? String(d.amount) : prev.amount,
        date: d.date || prev.date,
        categoryId: d.categoryId ? String(d.categoryId) : prev.categoryId,
      }));
      setOcrResult(`✅ Extracted: ${d.merchant ? `"${d.merchant}"` : ''} ${d.amount ? `₹${d.amount}` : ''} ${d.date || ''}`);
    } catch {
      setOcrResult('⚠️ Could not extract details — please fill manually');
    } finally {
      setOcrLoading(false);
      if (ocrFileRef.current) ocrFileRef.current.value = '';
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!detail || !window.confirm('Delete this line item?')) return;
    try { await deleteReimbursementItem(itemId); await refreshDetail(detail.id); }
    catch (err: any) { alert(extractErrorMessage(err)); }
  };

  const handleUploadToItem = async (itemId: number, files: File[]) => {
    if (files.length === 0) return;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await uploadReimbursementAttachment(itemId, fd);
      }
      if (detail) await refreshDetail(detail.id);
    } catch (err: any) { alert(err.response?.data?.message || 'Upload failed'); }
  };

  const handleDeleteAttachment = async (attId: number) => {
    if (!detail || !window.confirm('Delete this attachment?')) return;
    try { await deleteReimbursementAttachment(attId); await refreshDetail(detail.id); }
    catch (err: any) { alert(extractErrorMessage(err)); }
  };

  // ── Workflow ────────────────────────────────────────────────────────────────

  const handleReview = async (id: number) => {
    try { await reviewReimbursement(id); fetchAll(); if (detail?.id === id) await refreshDetail(id); }
    catch (err: any) {
      alert(extractErrorMessage(err));
      // Re-sync — the claim likely moved on; show its true status + buttons.
      if (detail?.id === id) await refreshDetail(id); else fetchAll();
    }
  };

  const handleReturn = async () => {
    if (!returnModal) return;
    try {
      await returnReimbursement(returnModal, { returnReason });
      const id = returnModal;
      setReturnModal(null); setReturnReason('');
      fetchAll(); if (detail?.id === id) await refreshDetail(id);
    } catch (err: any) {
      alert(extractErrorMessage(err));
      const id = returnModal;
      if (id && detail?.id === id) await refreshDetail(id); else fetchAll();
    }
  };

  const handleResubmit = async (id: number) => {
    try { await resubmitReimbursement(id); fetchAll(); if (detail?.id === id) await refreshDetail(id); }
    catch (err: any) { alert(extractErrorMessage(err)); }
  };

  const handleReopen = async (id: number) => {
    if (!window.confirm('Reopen this rejected claim? It goes back to the employee as Returned so it can be corrected and resubmitted.')) return;
    try { await reopenReimbursement(id); fetchAll(); if (detail?.id === id) await refreshDetail(id); }
    catch (err: any) { alert(extractErrorMessage(err)); }
  };

  const handleApprove = async (id: number) => {
    if (!window.confirm('Approve this claim? Expenses will be added to the task.')) return;
    try { await approveReimbursement(id); fetchAll(); if (detail?.id === id) await refreshDetail(id); }
    catch (err: any) {
      alert(extractErrorMessage(err));
      if (detail?.id === id) await refreshDetail(id); else fetchAll();
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      await rejectReimbursement(rejectModal, { rejectionReason: rejectReason });
      setRejectModal(null); setRejectReason(''); fetchAll();
    } catch (err: any) { alert(extractErrorMessage(err)); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this claim?')) return;
    try { await deleteReimbursement(id); fetchAll(); setDetail(null); }
    catch (err: any) { alert(extractErrorMessage(err)); }
  };

  const submitMarkPaid = async () => {
    if (!paidModal) return;
    if (!paidOn) { alert('Select a payment date'); return; }
    setBulkSaving(true);
    try {
      if (paidModal.isBulk) {
        const res = await bulkUpdateReimbursements({
          ids: paidModal.ids, action: 'MARK_PAID',
          paidOn, paymentMode, paymentReference,
        });
        alert(`Marked paid: ${res.data.succeeded} succeeded, ${res.data.failed} failed.`);
      } else {
        await markReimbursementPaid(paidModal.ids[0], { paidOn, paymentMode, paymentReference });
      }
      setPaidModal(null); setSelectedIds([]);
      setPaidOn(format(new Date(), 'yyyy-MM-dd')); setPaymentMode('BANK'); setPaymentReference('');
      fetchAll();
    } catch (err: any) {
      alert(extractErrorMessage(err));
    } finally { setBulkSaving(false); }
  };

  const handleBulkAction = async (action: 'REVIEW' | 'APPROVE' | 'REJECT' | 'DELETE') => {
    if (selectedIds.length === 0) return;
    let rejectionReason: string | undefined;
    if (action === 'REJECT') {
      rejectionReason = window.prompt('Rejection reason for all selected claims:') || '';
      if (rejectionReason === null) return;
    }
    if (action === 'DELETE' && !window.confirm(`Delete ${selectedIds.length} claim(s)?`)) return;
    if (action === 'APPROVE' && !window.confirm(`Approve ${selectedIds.length} claim(s)? Expenses will be added.`)) return;
    setBulkSaving(true);
    try {
      const res = await bulkUpdateReimbursements({ ids: selectedIds, action, rejectionReason });
      alert(`${action}: ${res.data.succeeded} succeeded, ${res.data.failed} failed.${
        res.data.failed > 0 ? '\n\nFirst few failures:\n' + (res.data.errors || []).slice(0, 5).map((e: any) => `  • #${e.id}: ${e.reason}`).join('\n') : ''
      }`);
      setSelectedIds([]);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Bulk action failed');
    } finally { setBulkSaving(false); }
  };

  const handleMarkPaidSingle = (id: number) => {
    setPaidModal({ ids: [id], isBulk: false });
    setPaidOn(format(new Date(), 'yyyy-MM-dd'));
    setPaymentMode('BANK');
    setPaymentReference('');
  };

  const handleExport = async () => {
    try {
      // Export what is on screen, filters and all — not the whole table.
      const res = await exportReimbursements({
        status: filterStatus || undefined,
        search: rSearch.trim() || undefined,
        submittedFrom: submittedFrom || undefined,
        submittedTo: submittedTo || undefined,
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reimbursements_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export failed'); }
  };

  // ── Filtered list ───────────────────────────────────────────────────────────

  const filtered = reimbursements.filter(r => {
    if (filterStatus && r.status !== filterStatus) return false;
    if ((submittedFrom || submittedTo) && !inDateRange(r.createdAt, submittedFrom, submittedTo)) return false;
    if (rSearch) {
      const q = rSearch.toLowerCase();
      // Every column the table actually shows is searchable — a claim is far
      // more often looked up by who it was for than by its claim number.
      if (!r.claimNumber.toLowerCase().includes(q) &&
          !r.staff.staffName.toLowerCase().includes(q) &&
          !r.task.taskId.toLowerCase().includes(q) &&
          !r.task.taskName.toLowerCase().includes(q) &&
          !(r.task.client?.clientName || '').toLowerCase().includes(q) &&
          !(r.task.category?.name || '').toLowerCase().includes(q) &&
          // "billed" / "not billed" / "partial", and the invoice a claim was
          // recovered on — so "M167" finds every claim billed on that invoice.
          !BILLING_LABEL[r.billingStatus || 'NA'].toLowerCase().includes(q) &&
          !(r.billedInvoiceNumbers || []).some(n => n.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const handleSort = (f: typeof sortField) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setPage(1);
  };
  const SortIcon = ({ f }: { f: typeof sortField }) =>
    sortField !== f ? <span className="ml-1 text-gray-300">↕</span>
    : <span className="ml-1 text-blue-600">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    if (sortField === 'total') {
      const diff = claimTotal(a) - claimTotal(b);
      return sortDir === 'asc' ? diff : -diff;
    }
    if (sortField === 'billing') {
      const diff = BILLING_RANK[a.billingStatus || 'NA'] - BILLING_RANK[b.billingStatus || 'NA'];
      return sortDir === 'asc' ? diff : -diff;
    }
    let av = '', bv = '';
    if (sortField === 'claimNumber') { av = a.claimNumber; bv = b.claimNumber; }
    else if (sortField === 'staff') { av = a.staff.staffName; bv = b.staff.staffName; }
    else if (sortField === 'status') { av = a.status; bv = b.status; }
    else if (sortField === 'createdAt') { av = a.createdAt; bv = b.createdAt; }
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  }), [filtered, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pagedReimbursements = sorted.slice((page - 1) * pageSize, page * pageSize);

  const pendingReview = reimbursements.filter(r => r.status === 'PENDING').length;
  const pendingApproval = reimbursements.filter(r => r.status === 'REVIEWED').length;
  const totalApproved = reimbursements.filter(r => r.status === 'APPROVED').reduce((s, r) => s + claimTotal(r), 0);
  const totalAwaitingPayment = reimbursements.filter(r => r.status === 'APPROVED').reduce((s, r) => s + claimTotal(r), 0);
  const totalPaid = reimbursements.filter(r => r.status === 'PAID').reduce((s, r) => s + claimTotal(r), 0);

  // Bulk select helpers
  const visibleIds = pagedReimbursements.map(r => r.id);
  const toggleSelect = (id: number) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    if (visibleIds.every(id => selectedIds.includes(id))) {
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };
  const selectedClaims = reimbursements.filter(r => selectedIds.includes(r.id));
  const canBulkReview = isHR && selectedClaims.every(r => r.status === 'PENDING') && selectedClaims.length > 0;
  const canBulkApprove = isPartnerOrAdmin && selectedClaims.every(r => r.status === 'REVIEWED') && selectedClaims.length > 0;
  const canBulkReject = isPartnerOrAdmin && selectedClaims.every(r => r.status === 'REVIEWED') && selectedClaims.length > 0;
  const canBulkMarkPaid = isHR && selectedClaims.every(r => r.status === 'APPROVED') && selectedClaims.length > 0;

  // canEdit: the claim owner can modify their claim (add/remove items, attach bills)
  // while it is still pending, under review, or returned. Editing a REVIEWED claim
  // sends it back to HR for re-review (handled server-side).
  const canEdit = (r: Reimbursement) =>
    ['PENDING', 'REVIEWED', 'RETURNED'].includes(r.status) && r.staff.id === user?.staffId;

  // canDelete: claim owner on PENDING/RETURNED, or admin on any PENDING/RETURNED
  const canDelete = (r: Reimbursement) =>
    ['PENDING', 'RETURNED'].includes(r.status) && (r.staff.id === user?.staffId || isAdmin);

  if (loading) return <p className="text-gray-500 text-sm">Loading...</p>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reimbursements</h2>
          <p className="text-sm text-gray-500 mt-1">Staff expense claims — approved amounts are added to task OPE</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary flex items-center gap-1.5" onClick={handleExport}>
            📥 Export Excel
          </button>
          <button className="btn-primary" onClick={openCreate}>+ New Claim</button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card text-center py-4">
          <p className="text-2xl font-bold text-yellow-600">{pendingReview}</p>
          <p className="text-xs text-gray-500 mt-1">Pending HR Review</p>
        </div>
        <div className="card text-center py-4">
          <p className="text-2xl font-bold text-blue-600">{pendingApproval}</p>
          <p className="text-xs text-gray-500 mt-1">Awaiting Approval</p>
        </div>
        <div className="card text-center py-4">
          <p className="text-2xl font-bold text-green-600">{reimbursements.filter(r => r.status === 'APPROVED').length}</p>
          <p className="text-xs text-gray-500 mt-1">Approved (Unpaid)</p>
        </div>
        <div className="card text-center py-4">
          <p className="text-xl font-bold text-blue-700">{fmt(totalAwaitingPayment)}</p>
          <p className="text-xs text-gray-500 mt-1">Awaiting Payment</p>
        </div>
        <div className="card text-center py-4">
          <p className="text-xl font-bold text-emerald-700">{fmt(totalPaid)}</p>
          <p className="text-xs text-gray-500 mt-1">Total Paid</p>
        </div>
      </div>

      {/* Alerts */}
      {isHR && pendingReview > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
          ⚠️ {pendingReview} claim{pendingReview > 1 ? 's' : ''} pending your HR review.
        </div>
      )}
      {isPartnerOrAdmin && pendingApproval > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
          ✅ {pendingApproval} reviewed claim{pendingApproval > 1 ? 's' : ''} awaiting your approval.
        </div>
      )}

      {/* Table */}
      <div className="card">
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <input className="input-field max-w-[240px]" placeholder="Search claim, staff, task, client…" value={rSearch}
            onChange={e => { setRSearch(e.target.value); setPage(1); }} />
          <select className="input-field max-w-[200px]" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="text-sm text-gray-400">{filtered.length} claim{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Bulk action bar */}
        {selectedIds.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.length} selected
            </span>
            <div className="flex gap-2 flex-wrap">
              {canBulkReview && (
                <button disabled={bulkSaving} onClick={() => handleBulkAction('REVIEW')}
                  className="text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 font-medium disabled:opacity-50">
                  ✓ Mark Reviewed ({selectedIds.length})
                </button>
              )}
              {canBulkApprove && (
                <button disabled={bulkSaving} onClick={() => handleBulkAction('APPROVE')}
                  className="text-xs px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 font-medium disabled:opacity-50">
                  ✓ Approve ({selectedIds.length})
                </button>
              )}
              {canBulkReject && (
                <button disabled={bulkSaving} onClick={() => handleBulkAction('REJECT')}
                  className="text-xs px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 font-medium disabled:opacity-50">
                  ✕ Reject ({selectedIds.length})
                </button>
              )}
              {canBulkMarkPaid && (
                <button disabled={bulkSaving} onClick={() => setPaidModal({ ids: selectedIds, isBulk: true })}
                  className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-medium disabled:opacity-50">
                  💰 Mark Paid ({selectedIds.length})
                </button>
              )}
              {(isAdmin || isPartnerOrAdmin) && (
                <button disabled={bulkSaving} onClick={() => handleBulkAction('DELETE')}
                  className="text-xs px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 font-medium disabled:opacity-50">
                  Delete ({selectedIds.length})
                </button>
              )}
              <button onClick={() => setSelectedIds([])} className="text-xs px-3 py-1 rounded bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium">
                Clear
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="table-header w-8">
                  {(isHR || isPartnerOrAdmin) && (
                    <input type="checkbox" className="w-4 h-4 rounded"
                      checked={visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id))}
                      onChange={toggleSelectAll} />
                  )}
                </th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('claimNumber')}>Claim # <SortIcon f="claimNumber" /></th>
                {showStaffColumn && <th className="table-header cursor-pointer select-none" onClick={() => handleSort('staff')}>Staff <SortIcon f="staff" /></th>}
                <th className="table-header">Task</th>
                <th className="table-header">Client</th>
                <th className="table-header">Category</th>
                <th className="table-header text-center cursor-pointer select-none" onClick={() => handleSort('billing')}>Billed <SortIcon f="billing" /></th>
                <th className="table-header text-right cursor-pointer select-none" onClick={() => handleSort('total')}>Total <SortIcon f="total" /></th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('status')}>Status <SortIcon f="status" /></th>
                <th className="table-header">
                  <DateRangeFilter
                    label="Submitted"
                    fromValue={submittedFrom}
                    toValue={submittedTo}
                    onChange={(f, t) => { setSubmittedFrom(f); setSubmittedTo(t); setPage(1); }}
                    sortable
                    onSort={() => handleSort('createdAt')}
                    sortIcon={<SortIcon f="createdAt" />}
                  />
                </th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedReimbursements.map(r => (
                <tr key={r.id} className={`hover:bg-gray-50 cursor-pointer ${selectedIds.includes(r.id) ? 'bg-blue-50' : ''}`} onClick={() => openDetail(r)}>
                  <td className="table-cell w-8" onClick={e => e.stopPropagation()}>
                    {(isHR || isPartnerOrAdmin) && (
                      <input type="checkbox" className="w-4 h-4 rounded"
                        checked={selectedIds.includes(r.id)}
                        onChange={() => toggleSelect(r.id)} />
                    )}
                  </td>
                  <td className="table-cell font-mono text-xs text-blue-700 font-medium">{r.claimNumber}</td>
                  {showStaffColumn && <td className="table-cell">{r.staff.staffName}</td>}
                  <td className="table-cell">
                    <div className="text-xs font-mono text-blue-600">{r.task.taskId}</div>
                    <div className="text-xs text-gray-500 truncate max-w-[130px]" title={r.task.taskName}>{r.task.taskName}</div>
                  </td>
                  <td className="table-cell text-xs text-gray-600 truncate max-w-[140px]" title={r.task.client?.clientName || ''}>
                    {r.task.client?.clientName || '—'}
                  </td>
                  <td className="table-cell text-xs text-gray-500 truncate max-w-[120px]" title={r.task.category?.name || ''}>
                    {r.task.category?.name || '—'}
                  </td>
                  <td className="table-cell text-center">
                    {(() => {
                      const bs = r.billingStatus || 'NA';
                      const invs = r.billedInvoiceNumbers || [];
                      return (
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${BILLING_COLOR[bs]}`}
                          title={
                            bs === 'NA' ? 'Not approved yet — nothing to bill'
                            : invs.length ? `Billed on ${invs.join(', ')}`
                            : 'No invoice has recovered this yet'
                          }
                        >
                          {BILLING_LABEL[bs]}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="table-cell text-right font-medium">{fmt(claimTotal(r))}</td>
                  <td className="table-cell">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="table-cell text-xs text-gray-500">{fmtDT(r.createdAt)}</td>
                  <td className="table-cell" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-2 flex-wrap">
                      {r.status === 'RETURNED' && r.staff.id === user?.staffId && (
                        <>
                          <button className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                            onClick={() => openDetail(r)}>Edit</button>
                          <button className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                            onClick={() => handleResubmit(r.id)}>Resubmit</button>
                        </>
                      )}
                      {r.status === 'REJECTED' && (r.staff.id === user?.staffId || isPartnerOrAdmin) && (
                        <button className="text-orange-600 hover:text-orange-800 text-xs font-medium"
                          onClick={() => handleReopen(r.id)}>Reopen</button>
                      )}
                      {isHR && r.status === 'PENDING' && r.staff.id !== user?.staffId && (
                        <>
                          <button className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                            onClick={() => handleReview(r.id)}>Mark Reviewed</button>
                          <button className="text-orange-600 hover:text-orange-800 text-xs font-medium"
                            onClick={() => { setReturnModal(r.id); setReturnReason(''); }}>Return</button>
                        </>
                      )}
                      {isPartnerOrAdmin && r.status === 'REVIEWED' && (
                        <>
                          <button className="text-green-600 hover:text-green-800 text-xs font-medium"
                            onClick={() => handleApprove(r.id)}>Approve</button>
                          <button className="text-orange-600 hover:text-orange-800 text-xs font-medium"
                            onClick={() => { setReturnModal(r.id); setReturnReason(''); }}>Send Back</button>
                          <button className="text-red-600 hover:text-red-800 text-xs font-medium"
                            onClick={() => { setRejectModal(r.id); setRejectReason(''); }}>Reject</button>
                        </>
                      )}
                      {isHR && r.status === 'APPROVED' && (
                        <button className="text-emerald-600 hover:text-emerald-800 text-xs font-medium"
                          onClick={() => handleMarkPaidSingle(r.id)}>💰 Mark Paid</button>
                      )}
                      {r.status === 'PAID' && r.paidOn && (
                        <span className="text-xs text-emerald-700" title={r.paymentReference || ''}>
                          Paid {fmtD(r.paidOn)}
                        </span>
                      )}
                      {canDelete(r) && (
                        <button className="text-gray-400 hover:text-gray-600 text-xs"
                          onClick={() => handleDelete(r.id)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={showStaffColumn ? 11 : 10} className="table-cell text-center text-gray-400 py-8">
                    No claims found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Rows:</span>
                <select className="border rounded px-2 py-1 text-sm" value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                <span className="px-3 py-1 text-sm">{page} / {totalPages}</span>
                <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Claim Modal ─────────────────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 pt-6 pb-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900">New Reimbursement Claim</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-5">

              {/* Step 1: Client search */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Client *</label>
                  <ClientSearch
                    value={selectedClientId}
                    onChange={handleClientChange}
                  />
                  <p className="text-xs text-gray-400 mt-1">Type to search by name or code</p>
                </div>

                {/* Step 2: Task (filtered to selected client) */}
                <div>
                  <label className="label">Task *</label>
                  <select
                    className="input-field"
                    value={claimForm.taskId}
                    onChange={e => setClaimForm({ ...claimForm, taskId: e.target.value })}
                    disabled={!selectedClientId}
                    required
                  >
                    <option value="">{selectedClientId ? '— Select open task —' : '— Select client first —'}</option>
                    {filteredTasks.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.taskId} — {t.taskName}{t.categoryName ? ` (${t.categoryName})` : ''}
                      </option>
                    ))}
                  </select>
                  {selectedClientId && filteredTasks.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No open tasks for this client.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="label">Claim Notes (optional)</label>
                <input className="input-field" value={claimForm.notes}
                  onChange={e => setClaimForm({ ...claimForm, notes: e.target.value })}
                  placeholder="Overall notes for this claim..." />
              </div>

              {/* Line items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Line Items *</label>
                  <button type="button" className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    onClick={() => setClaimForm({
                      ...claimForm,
                      items: [...claimForm.items, emptyItem(format(new Date(), 'yyyy-MM-dd'))],
                    })}>
                    + Add Row
                  </button>
                </div>

                <div className="space-y-3">
                  {claimForm.items.map((item, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                        <div className="md:col-span-2">
                          <label className="label text-xs">Description *</label>
                          <input className="input-field" value={item.description}
                            onChange={e => {
                              const items = [...claimForm.items];
                              items[idx] = { ...items[idx], description: e.target.value };
                              setClaimForm({ ...claimForm, items });
                            }} placeholder="e.g. Cab fare to client office" />
                        </div>
                        <div>
                          <label className="label text-xs">Category *</label>
                          <select className="input-field" value={item.categoryId}
                            onChange={e => {
                              const items = [...claimForm.items];
                              items[idx] = { ...items[idx], categoryId: e.target.value };
                              setClaimForm({ ...claimForm, items });
                            }}>
                            <option value="">— Select —</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="label text-xs">Date *</label>
                          <input type="date" className="input-field" value={item.date}
                            onChange={e => {
                              const items = [...claimForm.items];
                              items[idx] = { ...items[idx], date: e.target.value };
                              setClaimForm({ ...claimForm, items });
                            }} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 items-end">
                        <div>
                          <label className="label text-xs">Amount (₹) *</label>
                          <input type="number" min="0.01" step="0.01" className="input-field"
                            value={item.amount}
                            onChange={e => {
                              const items = [...claimForm.items];
                              items[idx] = { ...items[idx], amount: e.target.value };
                              setClaimForm({ ...claimForm, items });
                            }} placeholder="0.00" />
                        </div>
                        <div>
                          <label className="label text-xs">Bills / Receipts</label>
                          <input type="file" multiple accept="image/*,application/pdf"
                            className="block w-full text-xs text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700"
                            onChange={e => {
                              const files = Array.from(e.target.files || []);
                              if (files.length === 0) return;
                              // Accumulate across multiple picks instead of replacing —
                              // lets the user add bills from several folders/selections.
                              setItemFiles(prev => ({ ...prev, [idx]: [...(prev[idx] || []), ...files] }));
                              e.target.value = ''; // allow re-picking (even the same file)
                            }} />
                          {(itemFiles[idx]?.length || 0) > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {itemFiles[idx].map((f, fi) => (
                                <li key={fi} className="flex items-center gap-1 text-xs text-gray-600">
                                  <span className="truncate max-w-[160px]" title={f.name}>📎 {f.name}</span>
                                  <button type="button" className="text-red-400 hover:text-red-600 shrink-0"
                                    onClick={() => setItemFiles(prev => ({
                                      ...prev, [idx]: (prev[idx] || []).filter((_, i) => i !== fi),
                                    }))}>✕</button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                      {claimForm.items.length > 1 && (
                        <button type="button" className="mt-2 text-red-500 hover:text-red-700 text-xs"
                          onClick={() => {
                            const items = claimForm.items.filter((_, i) => i !== idx);
                            const newFiles: Record<number, File[]> = {};
                            Object.entries(itemFiles).forEach(([k, v]) => {
                              const ki = Number(k);
                              if (ki < idx) newFiles[ki] = v;
                              else if (ki > idx) newFiles[ki - 1] = v;
                            });
                            setClaimForm({ ...claimForm, items });
                            setItemFiles(newFiles);
                          }}>
                          Remove row
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-3 text-right text-sm font-semibold text-gray-700">
                  Claim Total: {fmt(claimForm.items.reduce((s, i) => s + (Number(i.amount) || 0), 0))}
                </div>
              </div>

              {createError && <p className="text-red-600 text-sm">{createError}</p>}
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={creating}>
                  {creating ? 'Submitting...' : 'Submit Claim'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Detail Modal ───────────────────────────────────────────────────────── */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 pt-5 pb-3 border-b flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-blue-700 font-bold">{detail.claimNumber}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLOR[detail.status]}`}>
                    {STATUS_LABEL[detail.status]}
                  </span>
                  {detail.billingStatus && detail.billingStatus !== 'NA' && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BILLING_COLOR[detail.billingStatus]}`}>
                      {BILLING_LABEL[detail.billingStatus]}
                      {!!detail.billedInvoiceNumbers?.length && ` · ${detail.billedInvoiceNumbers.join(', ')}`}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {detail.task.taskId} — {detail.task.taskName}
                  {detail.task.category?.name && <> ({detail.task.category.name})</>}
                  {detail.task.client?.clientName && <> &nbsp;·&nbsp; {detail.task.client.clientName}</>}
                  &nbsp;·&nbsp; by {detail.staff.staffName} &nbsp;·&nbsp; {fmtDT(detail.createdAt)}
                </p>
              </div>
              <button className="text-gray-400 hover:text-gray-600 text-xl leading-none" onClick={() => setDetail(null)}>✕</button>
            </div>

            <div className="p-6 space-y-5">
              {detail.notes && (
                <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-700">
                  <span className="font-medium">Notes: </span>{detail.notes}
                </div>
              )}

              {/* Status trail */}
              {detail.status === 'RETURNED' && detail.returnReason && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-sm text-orange-800">
                  <strong>Returned{detail.reviewedBy ? ` by ${detail.reviewedBy.staffName}` : ''}:</strong> {detail.returnReason}
                  {detail.staff.id === user?.staffId && <div className="mt-1 text-xs">Correct the items or bills below (use Edit on a line), then press Resubmit.</div>}
                </div>
              )}
              {detail.status === 'REJECTED' && detail.rejectionReason && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
                  <strong>Rejected by {detail.approvedBy?.staffName}:</strong> {detail.rejectionReason}
                </div>
              )}
              {['REVIEWED', 'APPROVED'].includes(detail.status) && detail.reviewedBy && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
                  ✔ Reviewed by <strong>{detail.reviewedBy.staffName}</strong> on {fmtDT(detail.reviewedAt)}
                </div>
              )}
              {detail.status === 'APPROVED' && detail.approvedBy && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800">
                  ✔ Approved by <strong>{detail.approvedBy.staffName}</strong> on {fmtDT(detail.approvedAt)}
                </div>
              )}

              {/* Re-review warning — editing a reviewed claim sends it back to HR */}
              {canEdit(detail) && detail.status === 'REVIEWED' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                  ⚠️ This claim has already been reviewed by HR. Any change (items or bills) will send it back to <strong>Pending Review</strong>.
                </div>
              )}

              {/* Line items */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Line Items</h4>
                <div className="space-y-3">
                  {detail.items.map(item => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-gray-800">{item.description}</span>
                            <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{item.category.name}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{fmtD(item.date)}</div>
                          {/* Recovery state for this exact expense. Shown per
                              item because a claim can be part-billed. */}
                          {item.billing?.billable && (
                            <div className="text-xs mt-1">
                              {item.billing.billed ? (
                                <span className="text-emerald-700">
                                  Billed on <span className="font-medium">{item.billing.invoiceNumber}</span>
                                  {item.billing.invoiceDate && <> · {fmtD(item.billing.invoiceDate)}</>}
                                  {item.billing.invoiceStatus && <> · {item.billing.invoiceStatus}</>}
                                </span>
                              ) : (
                                <span className="text-amber-700">Not yet billed to the client</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-semibold text-gray-800">{fmt(item.amount)}</div>
                          {canEdit(detail) && (
                            <div className="flex gap-2 justify-end mt-0.5">
                              <button className="text-xs text-indigo-600 hover:text-indigo-800"
                                onClick={() => startEditItem(item)}>Edit</button>
                              <button className="text-xs text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteItem(item.id)}>Remove</button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Attachments */}
                      {item.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {item.attachments.map(att => (
                            <div key={att.id} className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                              <a href={uploadsUrl(att.fileName)} target="_blank" rel="noreferrer"
                                className="text-blue-600 text-xs hover:underline">
                                {att.mimeType.startsWith('image/') ? '🖼' : '📄'} {att.originalName}
                              </a>
                              {canEdit(detail) && (
                                <button className="text-red-400 hover:text-red-600 text-xs ml-1"
                                  onClick={() => handleDeleteAttachment(att.id)}>✕</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {canEdit(detail) && (
                        <div className="mt-2">
                          <label className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer font-medium">
                            + Attach bill(s)
                            <input type="file" multiple className="hidden" accept="image/*,application/pdf"
                              onChange={async e => {
                                const files = Array.from(e.target.files || []);
                                if (files.length) await handleUploadToItem(item.id, files);
                                e.target.value = '';
                              }} />
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-right text-sm font-bold text-gray-800">
                  Total: {fmt(claimTotal(detail))}
                </div>
              </div>

              {/* Add item (only while editable) */}
              {canEdit(detail) && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">{editingItemId ? 'Edit Line Item' : 'Add Line Item'}</h4>
                    <div className="flex items-center gap-2">
                      <label className={`flex items-center gap-1.5 cursor-pointer text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${ocrLoading ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'}`}>
                        {ocrLoading ? '⏳ Scanning...' : '📷 Scan Receipt (OCR)'}
                        <input ref={ocrFileRef} type="file" accept="image/*,application/pdf" className="hidden"
                          onChange={handleOcrUpload} disabled={ocrLoading} />
                      </label>
                    </div>
                  </div>
                  {ocrResult && (
                    <div className={`text-xs px-3 py-2 rounded-lg mb-3 ${ocrResult.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                      {ocrResult}
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                    <div className="md:col-span-2">
                      <label className="label text-xs">Description</label>
                      <input className="input-field" value={addItemForm.description}
                        onChange={e => setAddItemForm({ ...addItemForm, description: e.target.value })} />
                    </div>
                    <div>
                      <label className="label text-xs">Category</label>
                      <select className="input-field" value={addItemForm.categoryId}
                        onChange={e => setAddItemForm({ ...addItemForm, categoryId: e.target.value })}>
                        <option value="">— Select —</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label text-xs">Date</label>
                      <input type="date" className="input-field" value={addItemForm.date}
                        onChange={e => setAddItemForm({ ...addItemForm, date: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="label text-xs">Amount (₹)</label>
                      <input type="number" min="0.01" step="0.01" className="input-field"
                        value={addItemForm.amount}
                        onChange={e => setAddItemForm({ ...addItemForm, amount: e.target.value })} placeholder="0.00" />
                    </div>
                    <div>
                      <label className="label text-xs">Attachments (optional)</label>
                      <input ref={addFileRef} type="file" multiple accept="image/*,application/pdf"
                        className="block w-full text-xs text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700"
                        onChange={e => {
                          const files = Array.from(e.target.files || []);
                          if (files.length === 0) return;
                          setAddItemFiles(prev => [...prev, ...files]);
                          e.target.value = '';
                        }} />
                      {addItemFiles.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {addItemFiles.map((f, fi) => (
                            <li key={fi} className="flex items-center gap-1 text-xs text-gray-600">
                              <span className="truncate max-w-[160px]" title={f.name}>📎 {f.name}</span>
                              <button type="button" className="text-red-400 hover:text-red-600 shrink-0"
                                onClick={() => setAddItemFiles(prev => prev.filter((_, i) => i !== fi))}>✕</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {addItemError && <p className="text-red-600 text-xs mb-2">{addItemError}</p>}
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary text-sm" onClick={handleAddItem}>{editingItemId ? 'Save changes' : '+ Add Item'}</button>
                    {editingItemId && <button type="button" className="btn-secondary text-sm" onClick={cancelEditItem}>Cancel</button>}
                  </div>
                </div>
              )}

              {/* Action bar */}
              <div className="border-t pt-4 flex flex-wrap gap-2 justify-end">
                {isHR && detail.status === 'PENDING' && detail.staff.id !== user?.staffId && (
                  <>
                    <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 font-medium"
                      onClick={() => handleReview(detail.id)}>✔ Mark Reviewed</button>
                    <button className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 font-medium"
                      onClick={() => { setReturnModal(detail.id); setReturnReason(''); }}>↩ Return to Staff</button>
                  </>
                )}
                {isPartnerOrAdmin && detail.status === 'REVIEWED' && (
                  <>
                    <button className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 font-medium"
                      onClick={() => handleApprove(detail.id)}>✔ Approve Claim</button>
                    <button className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 font-medium"
                      onClick={() => { setReturnModal(detail.id); setReturnReason(''); }}>↩ Send Back to Staff</button>
                    <button className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 font-medium"
                      onClick={() => { setRejectModal(detail.id); setRejectReason(''); }}>✕ Reject</button>
                  </>
                )}
                {detail.status === 'RETURNED' && detail.staff.id === user?.staffId && (
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 font-medium"
                    onClick={() => handleResubmit(detail.id)}>↪ Resubmit for review</button>
                )}
                {detail.status === 'REJECTED' && (detail.staff.id === user?.staffId || isPartnerOrAdmin) && (
                  <button className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 font-medium"
                    onClick={() => handleReopen(detail.id)}>↺ Reopen claim</button>
                )}
                {isHR && detail.status === 'APPROVED' && (
                  <button className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-700 font-medium"
                    onClick={() => handleMarkPaidSingle(detail.id)}>💰 Mark as Paid</button>
                )}
                {detail.status === 'PAID' && (
                  <div className="text-sm text-emerald-700 mr-auto">
                    Paid {fmtD(detail.paidOn)}{detail.paymentMode ? ` via ${detail.paymentMode}` : ''}{detail.paymentReference ? ` (Ref: ${detail.paymentReference})` : ''}
                  </div>
                )}
                {canDelete(detail) && (
                  <button className="btn-secondary text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleDelete(detail.id)}>Delete Claim</button>
                )}
                <button className="btn-secondary" onClick={() => setDetail(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Return Reason Modal ────────────────────────────────────────────────── */}
      {returnModal !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Return to Staff</h3>
            <label className="label">Comments for staff *</label>
            <textarea className="input-field" rows={3} value={returnReason}
              onChange={e => setReturnReason(e.target.value)}
              placeholder="What needs to be corrected or clarified?" />
            <div className="flex gap-3 justify-end mt-4">
              <button className="btn-secondary" onClick={() => setReturnModal(null)}>Cancel</button>
              <button className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 font-medium"
                onClick={handleReturn}>Return Claim</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mark Paid Modal (single or bulk) ─────────────────────────────────── */}
      {paidModal !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              💰 Mark {paidModal.isBulk ? `${paidModal.ids.length} Claims` : 'Claim'} as Paid
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              {paidModal.isBulk
                ? `${paidModal.ids.length} approved claims will be marked PAID.`
                : 'Approved claim will be marked PAID.'}
            </p>
            <div className="space-y-3">
              <div>
                <label className="label">Payment Date *</label>
                <input type="date" className="input-field" value={paidOn}
                  onChange={e => setPaidOn(e.target.value)} required />
              </div>
              <div>
                <label className="label">Payment Mode</label>
                <select className="input-field" value={paymentMode} onChange={e => setPaymentMode(e.target.value)}>
                  <option value="BANK">Bank Transfer</option>
                  <option value="CASH">Cash</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="UPI">UPI</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="label">Reference (UTR / Cheque #)</label>
                <input className="input-field" value={paymentReference}
                  onChange={e => setPaymentReference(e.target.value)}
                  placeholder="Optional reference number" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button className="btn-secondary" onClick={() => setPaidModal(null)}>Cancel</button>
              <button disabled={bulkSaving || !paidOn}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-700 font-medium disabled:opacity-50"
                onClick={submitMarkPaid}>
                {bulkSaving ? 'Saving…' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ───────────────────────────────────────────────────────── */}
      {rejectModal !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Reject Claim</h3>
            <label className="label">Reason (optional)</label>
            <textarea className="input-field" rows={3} value={rejectReason}
              onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." />
            <div className="flex gap-3 justify-end mt-4">
              <button className="btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button>
              <button className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 font-medium"
                onClick={handleReject}>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// The desktop table and its six modals have nothing to give a 375px screen,
// so a phone gets a purpose-built screen instead. Split at the wrapper rather
// than early-returning inside the component: the desktop body runs a long list
// of hooks, and skipping them on a width change would break the rules of hooks.
const Reimbursements: React.FC = () => (useIsMobile() ? <MobileReimbursements /> : <DesktopReimbursements />);

export default Reimbursements;
